/**
 * The airship's mooring at the boat landing (ADR 0032, the designer's Q42:
 * the dungeon is flown to, not walked to; not in the lantern-water
 * prototype). It stands in the landing's opening in the coping, north of the
 * steps, so nothing crosses a coping block or a parapet pier:
 *
 * - a gangway whose foot rests on a timber sill on the quay (its deck above
 *   the coping) and which rises east over the water, a rail on each side;
 * - a davit on the quay behind it, its arm leaning out over the water: a
 *   bridle from the arm's tip holds the gangway's far end, and the mooring
 *   ring hangs at the tip;
 * - an iron bollard on the quay, the mooring rope from its neck to the ring;
 * - a sign board hanging from a post's arm, an airship painted on it (no
 *   lettering), so the spot reads as the airship's even before slice 7 brings
 *   the ship.
 *
 * Built from the kit's materials only (iron, timber, cream, dark, the canvas
 * rope), so it merges with the town's batches and adds no light. Origin: the
 * gangway's foot on the quay, the middle of its width; the gangway runs east
 * (+x). Its `board` interactable, the spawn of the same id and the colliders
 * (bollard, davit, sign post, the gangway's foot) are the scene record's. No
 * params; its dimensions are `sceneLook.airshipMooring` (`constants.ts`).
 */
import * as THREE from 'three';
import { sceneLook } from '../constants.ts';
import type { Vec3 } from '../kit.ts';
import type { Prefab } from './types.ts';

const { quayTop, bollard, gangway, davit, rope, sign } = sceneLook.airshipMooring;

export const prefab: Prefab = {
  id: 'airship_mooring',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    root.name = 'airship-mooring';
    const { box, cylinder, line, mesh, m, kit, sphere } = ctx;

    // The gangway: its foot on a sill on the quay, rising east over the water.
    const slope = Math.tan((gangway.rise * Math.PI) / 180);
    const foot = quayTop + gangway.sill.height;
    const deckAt = (x: number): number => foot + gangway.thickness + x * slope;
    const half = gangway.width / 2;
    box(root, gangway.sill.depth / 2, quayTop + gangway.sill.height / 2, 0, gangway.sill.depth, gangway.sill.height, gangway.width + 2 * gangway.rail.post, m.darkWood);
    const deck = new THREE.Group();
    deck.position.set(0, foot, 0);
    deck.rotation.z = (gangway.rise * Math.PI) / 180;
    root.add(deck);
    const run = gangway.length / Math.cos((gangway.rise * Math.PI) / 180);
    const planks = Math.round(run / gangway.plank);
    const pitch = run / planks;
    for (let i = 0; i < planks; i += 1) {
      box(deck, (i + 0.5) * pitch, gangway.thickness / 2, 0, pitch * gangway.timber, gangway.thickness, gangway.width, m.wood);
    }
    for (const side of [-1, 1]) {
      box(deck, run / 2, -gangway.stringer / 2, side * (half - gangway.stringer / 2), run, gangway.stringer, gangway.stringer, m.darkWood);
    }
    const { rail } = gangway;
    const first = rail.inset;
    const last = gangway.length - rail.inset;
    const top = (x: number): number => deckAt(x) + rail.height;
    for (const side of [-1, 1]) {
      const z = side * half;
      for (let i = 0; i < rail.posts; i += 1) {
        const x = first + ((last - first) * i) / (rail.posts - 1);
        cylinder(root, x, (deckAt(x) + top(x)) / 2, z, rail.post, rail.height, m.darkWood);
      }
      line(root, [first, top(first), z], [last, top(last), z], rail.thickness, m.darkWood);
    }

    // The davit: a mast on the quay behind the gangway, its arm leaning out over the water.
    const mastTop: Vec3 = [davit.x, davit.height, davit.z];
    const tip: Vec3 = [davit.reach, davit.height - davit.droop, 0];
    cylinder(root, davit.x, davit.height / 2, davit.z, davit.radius, davit.height, m.iron, davit.taper);
    line(root, mastTop, tip, davit.arm, m.iron);
    const brace = (t: number): Vec3 => [mastTop[0] + (tip[0] - mastTop[0]) * t, mastTop[1] + (tip[1] - mastTop[1]) * t, mastTop[2] + (tip[2] - mastTop[2]) * t];
    line(root, [davit.x, davit.height * davit.brace.from, davit.z], brace(davit.brace.to), davit.brace.thickness, m.iron);
    for (const side of [-1, 1]) line(root, tip, [last, top(last), side * half], davit.bridle, m.iron);
    const ringGeo = kit.shared('airship-mooring:ring', () => new THREE.TorusGeometry(davit.ring.radius, davit.ring.tube, 6, 16));
    const ring: Vec3 = [tip[0], tip[1] - davit.ring.radius, tip[2]];
    mesh(root, ringGeo, m.iron, ...ring);

    // The bollard, and the mooring rope from its neck to the ring, sagging a little.
    cylinder(root, bollard.x, quayTop + bollard.height / 2, bollard.z, bollard.radius, bollard.height, m.iron, bollard.taper);
    cylinder(root, bollard.x, quayTop + bollard.height + bollard.capHeight / 2, bollard.z, bollard.capRadius, bollard.capHeight, m.iron);
    const neck: Vec3 = [bollard.x, quayTop + bollard.height * bollard.neck, bollard.z];
    const hook: Vec3 = [ring[0], ring[1] - davit.ring.radius, ring[2]];
    const middle: Vec3 = [(neck[0] + hook[0]) / 2, (neck[1] + hook[1]) / 2 - rope.sag, (neck[2] + hook[2]) / 2];
    line(root, neck, middle, rope.radius, m.canvas);
    line(root, middle, hook, rope.radius, m.canvas);

    // The sign: a board hanging from the post's arm (reaching west), an airship painted on its face.
    cylinder(root, sign.x, quayTop + sign.height / 2, sign.z, sign.radius, sign.height, m.darkWood);
    const armY = quayTop + sign.height - sign.arm.below;
    line(root, [sign.x, armY, sign.z], [sign.x - sign.arm.reach, armY, sign.z], sign.arm.thickness, m.iron);
    const { board, glyph } = sign;
    const bx = sign.x - board.at;
    const by = armY - board.drop - board.height / 2;
    for (const side of [-1, 1]) {
      const cx = bx + side * board.chainSpread;
      line(root, [cx, armY, sign.z], [cx, by + board.height / 2, sign.z], board.chain, m.iron);
    }
    box(root, bx, by, sign.z, board.width, board.height, board.depth, m.darkWood);
    const face = sign.z + board.depth / 2;
    box(root, bx, by, face, board.width - 2 * board.border, board.height - 2 * board.border, glyph.relief, m.cream);
    const paint = face + glyph.relief;
    mesh(root, sphere, m.dark, bx, by + glyph.hull.y, paint, glyph.hull.rx, glyph.hull.ry, glyph.relief);
    box(root, bx, by + glyph.gondola.y, paint, glyph.gondola.width, glyph.gondola.height, glyph.relief, m.dark);
    box(root, bx + glyph.fin.x, by + glyph.fin.y, paint, glyph.fin.width, glyph.fin.height, glyph.relief, m.dark);
    for (const side of [-1, 1]) {
      const sx = bx + side * glyph.strut.spread;
      line(root, [sx, by + glyph.hull.y - glyph.hull.ry * glyph.strut.from, paint], [sx, by + glyph.gondola.y, paint], glyph.strut.width, m.dark);
    }
    return { root, sockets: { gangway: [gangway.length, deckAt(gangway.length), 0], ring } };
  },
};
